import { useState, useEffect } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { ArrowLeft, Plus, X, Save } from 'lucide-react'
import evaluationGridService from '../../../services/evaluationGridService'
import classService from '../../../services/classService'

interface Criterion {
  name: string
  coefficient: number
}

export function EditEvaluationGridPage() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const queryClient = useQueryClient()

  const [name, setName] = useState('')
  const [classId, setClassId] = useState('')
  const [academicYear, setAcademicYear] = useState('')
  const [semester, setSemester] = useState('S1')
  const [criteria, setCriteria] = useState<Criterion[]>([{ name: '', coefficient: 1 }])
  const [errors, setErrors] = useState<string[]>([])

  // Récupérer la grille
  const { data: grid, isLoading: loadingGrid } = useQuery({
    queryKey: ['evaluationGrid', id],
    queryFn: () => evaluationGridService.getById(id!),
    enabled: !!id
  })

  // Récupérer les classes
  const { data: classes } = useQuery({
    queryKey: ['classes'],
    queryFn: () => classService.getAll()
  })

  // Remplir le formulaire avec les données de la grille
  useEffect(() => {
    if (grid) {
      setName(grid.name)
      setClassId(grid.classId)
      setAcademicYear(grid.academicYear)
      setSemester(grid.semester)
      setCriteria(grid.criteria?.map((c: any) => ({ name: c.name, coefficient: c.coefficient })) || [])
    }
  }, [grid])

  const updateMutation = useMutation({
    mutationFn: (data: any) => evaluationGridService.update(id!, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['evaluationGrids'] })
      queryClient.invalidateQueries({ queryKey: ['evaluationGrid', id] })
      navigate('/modules/evaluation/grids')
    }
  })

  const addCriterion = () => {
    setCriteria([...criteria, { name: '', coefficient: 1 }])
  }

  const removeCriterion = (index: number) => {
    setCriteria(criteria.filter((_, i) => i !== index))
  }

  const updateCriterion = (index: number, field: keyof Criterion, value: string | number) => {
    const updated = [...criteria]
    updated[index] = { ...updated[index], [field]: value }
    setCriteria(updated)
  }

  const validate = () => {
    const newErrors: string[] = []
    if (!name.trim()) newErrors.push('Le nom de la grille est requis')
    if (!classId) newErrors.push('Veuillez sélectionner une classe')
    if (!academicYear.trim()) newErrors.push('L\'année scolaire est requise')
    if (criteria.length === 0) newErrors.push('Au moins un critère est requis')
    if (criteria.some(c => !c.name.trim())) newErrors.push('Tous les critères doivent avoir un nom')
    if (criteria.some(c => c.coefficient <= 0)) newErrors.push('Les coefficients doivent être supérieurs à 0')
    setErrors(newErrors)
    return newErrors.length === 0
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!validate()) return

    await updateMutation.mutateAsync({
      name,
      classId,
      academicYear,
      semester,
      criteria
    })
  }

  if (loadingGrid) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-violet-50 via-purple-50 to-indigo-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-violet-600"></div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-violet-50 via-purple-50 to-indigo-50">
      <header className="bg-white/80 backdrop-blur-sm border-b border-gray-200 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <button
            onClick={() => navigate('/modules/evaluation/grids')}
            className="flex items-center gap-2 text-gray-600 hover:text-gray-900 transition-colors"
          >
            <ArrowLeft className="w-5 h-5" />
            <span>Retour aux grilles</span>
          </button>
        </div>
      </header>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Éditer la grille d'évaluation</h1>
          <p className="text-gray-600">Modifiez les paramètres et critères de votre grille</p>
        </div>

        {errors.length > 0 && (
          <div className="mb-6 bg-red-50 border border-red-200 rounded-lg p-4">
            <ul className="list-disc list-inside text-red-700 text-sm">
              {errors.map((error, idx) => (
                <li key={idx}>{error}</li>
              ))}
            </ul>
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="bg-white/80 backdrop-blur-sm rounded-xl border border-gray-200 p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Informations générales</h2>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Nom de la grille</label>
                <input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-violet-500 focus:border-transparent"
                  placeholder="Ex: Évaluation Comportementale S1"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Classe</label>
                <select
                  value={classId}
                  onChange={(e) => setClassId(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-violet-500 focus:border-transparent"
                >
                  <option value="">Sélectionnez une classe</option>
                  {classes?.map((cls: any) => (
                    <option key={cls.id} value={cls.id}>{cls.name}</option>
                  ))}
                </select>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Année scolaire</label>
                  <input
                    type="text"
                    value={academicYear}
                    onChange={(e) => setAcademicYear(e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-violet-500 focus:border-transparent"
                    placeholder="Ex: 2024-2025"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Semestre</label>
                  <select
                    value={semester}
                    onChange={(e) => setSemester(e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-violet-500 focus:border-transparent"
                  >
                    <option value="S1">Semestre 1</option>
                    <option value="S2">Semestre 2</option>
                  </select>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-white/80 backdrop-blur-sm rounded-xl border border-gray-200 p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-gray-900">Critères d'évaluation</h2>
              <button
                type="button"
                onClick={addCriterion}
                className="flex items-center gap-2 px-3 py-2 bg-violet-50 text-violet-700 rounded-lg hover:bg-violet-100 transition-colors"
              >
                <Plus className="w-4 h-4" />
                <span className="text-sm font-medium">Ajouter</span>
              </button>
            </div>

            <div className="space-y-3">
              {criteria.map((criterion, index) => (
                <div key={index} className="flex gap-3 items-start">
                  <div className="flex-1">
                    <input
                      type="text"
                      value={criterion.name}
                      onChange={(e) => updateCriterion(index, 'name', e.target.value)}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-violet-500 focus:border-transparent"
                      placeholder="Nom du critère"
                    />
                  </div>
                  <div className="w-32">
                    <input
                      type="number"
                      min="1"
                      step="1"
                      value={criterion.coefficient}
                      onChange={(e) => updateCriterion(index, 'coefficient', parseInt(e.target.value) || 1)}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-violet-500 focus:border-transparent"
                      placeholder="Coef."
                    />
                  </div>
                  {criteria.length > 1 && (
                    <button
                      type="button"
                      onClick={() => removeCriterion(index)}
                      className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                    >
                      <X className="w-5 h-5" />
                    </button>
                  )}
                </div>
              ))}
            </div>
          </div>

          <div className="flex gap-3">
            <button
              type="button"
              onClick={() => navigate('/modules/evaluation/grids')}
              className="flex-1 px-6 py-3 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors font-medium"
            >
              Annuler
            </button>
            <button
              type="submit"
              disabled={updateMutation.isPending}
              className="flex-1 flex items-center justify-center gap-2 px-6 py-3 bg-gradient-to-r from-violet-600 to-purple-600 text-white rounded-lg hover:from-violet-700 hover:to-purple-700 transition-all shadow-lg hover:shadow-xl font-medium disabled:opacity-50"
            >
              {updateMutation.isPending ? (
                <>
                  <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  <span>Enregistrement...</span>
                </>
              ) : (
                <>
                  <Save className="w-5 h-5" />
                  <span>Enregistrer les modifications</span>
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}
